import { Link, useLocation } from "wouter";
import { ChevronLeft, ChevronRight, Loader2, Trophy } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  useLeaderboardByRange,
  useRoster,
  useStatsPending,
} from "@/lib/queries";
import { useStickyState } from "@/lib/useStickyState";
import type { LeaderboardRange } from "@/lib/types";

// Full leaderboard page. The dashboard's "Top of the board" card
// links here as "Full ›". Reuses the server-cached by-range
// leaderboard (stats pipeline) so the rendering is O(1) once the
// worker has primed the cache. Adds:
//   - all rows (not the dashboard's top-5 cap)
//   - extra columns: games played + point diff
//   - per-row links into the player profile (drill-in)
//   - same sticky date-range preference key as the dashboard so the
//     two pages stay in sync without an extra state hop.

type RangeTab = LeaderboardRange;

const RANGE_OPTIONS: { id: RangeTab; label: string }[] = [
  { id: "1d", label: "Today" },
  { id: "7d", label: "7d" },
  { id: "30d", label: "30d" },
  { id: "90d", label: "90d" },
  { id: "all", label: "All" },
];

export default function Leaderboards() {
  const [rangeTab, setRangeTab] = useStickyState<RangeTab>(
    "dashboard.rangeTab",
    "30d",
  );
  const [, setLocation] = useLocation();
  const { data: payload, isPending } = useLeaderboardByRange(rangeTab);
  const { data: roster = [] } = useRoster();
  const { data: pending } = useStatsPending();
  const isUpdating = (pending?.pending ?? 0) > 0;

  // Slug-by-id fallback for old cache rows that predate slug being in the
  // payload; new payloads carry row.slug directly. Each row links into the
  // player profile.
  const slugById = new Map(roster.map((p) => [p.id, p.slug] as const));

  const rows = payload?.rows ?? [];

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-4xl mx-auto space-y-6">
      <header className="space-y-1">
        <div>
          <Link
            href="/dashboard"
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            <ChevronLeft size={12} /> Dashboard
          </Link>
        </div>
        <h1 className="text-2xl sm:text-3xl font-display uppercase tracking-tight flex items-center gap-2">
          <Trophy size={22} className="text-primary shrink-0" />
          <span>Leaderboards</span>
          {isUpdating ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-display uppercase tracking-wider text-muted-foreground bg-muted/40 border border-border px-1.5 py-0.5 rounded">
              <Loader2 size={10} className="animate-spin" />
              Updating
            </span>
          ) : null}
        </h1>
        <p className="text-muted-foreground text-sm">
          {rangeTab === "all"
            ? "All-time rating across this org."
            : rangeTab === "1d"
              ? "Today's board — a few games can crown a day leader."
              : `Ratings over the last ${rangeTab.replace("d", " days")}.`}
        </p>
      </header>

      <Card>
        <CardHeader className="space-y-3">
          <div>
            <CardTitle className="text-base">Top of the board</CardTitle>
            <CardDescription>
              {payload
                ? `${payload.sourceGameCount} completed game${payload.sourceGameCount === 1 ? "" : "s"} in window`
                : "Loading…"}
            </CardDescription>
          </div>
          <TabStrip
            options={RANGE_OPTIONS}
            value={rangeTab}
            onChange={setRangeTab}
          />
        </CardHeader>
        <CardContent className="p-0">
          {isPending ? (
            <Empty hint="Loading…" />
          ) : rows.length === 0 ? (
            <Empty
              hint={
                rangeTab === "all"
                  ? "Play a few games to start the rankings."
                  : rangeTab === "1d"
                    ? "No games played today yet. Get one in to lead the day!"
                    : `No completed games in the last ${rangeTab.replace("d", " days")}. Try "All".`
              }
            />
          ) : (
            <div className="overflow-hidden">
              {/* Header row — visible on sm+ where horizontal real
                  estate allows; collapses to a compact stack on
                  mobile (the row layout below adapts). */}
              <div className="hidden sm:grid grid-cols-[2rem_1fr_3rem_4rem_4rem_4rem_4rem] gap-3 px-3 py-2 text-[10px] font-display uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/20">
                <div>#</div>
                <div>Player</div>
                <div className="text-right">G</div>
                <div className="text-right">W–L</div>
                <div className="text-right">Win %</div>
                <div className="text-right">PPR</div>
                <div
                  className="text-right text-foreground"
                  title="Rating — a sample-corrected blend of win rate, scoring margin, and points-per-round. Play more games to firm it up."
                >
                  Rating
                </div>
              </div>
              <ul className="divide-y divide-border">
                {rows.map((row, idx) => {
                  const rank = idx + 1;
                  const slug = row.slug ?? slugById.get(row.playerId);
                  const profileHref = slug
                    ? `/dashboard/players/${slug}`
                    : null;
                  const go = () => profileHref && setLocation(profileHref);
                  return (
                    <li
                      key={row.playerId}
                      onClick={go}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          go();
                        }
                      }}
                      role={profileHref ? "link" : undefined}
                      tabIndex={profileHref ? 0 : undefined}
                      aria-label={
                        profileHref ? `${row.name} — view profile` : undefined
                      }
                      className={
                        // Mobile: 3 columns (rank · name · rating). The extra
                        // stat columns are hidden below sm and the grid widens
                        // to the full 7-column layout at sm+.
                        "grid grid-cols-[2rem_1fr_4rem] sm:grid-cols-[2rem_1fr_3rem_4rem_4rem_4rem_4rem] gap-3 px-3 py-2.5 text-sm items-center tabular-nums " +
                        (profileHref
                          ? "cursor-pointer hover:bg-muted/40 active:bg-muted/60 transition-colors focus:outline-none focus-visible:bg-muted/40"
                          : "")
                      }
                    >
                      <div className="font-display text-base">{rank}</div>
                      <div className="min-w-0">
                        <span className="font-medium truncate inline-flex items-center gap-1 max-w-full">
                          {row.name}
                          {profileHref && (
                            <ChevronRight
                              size={13}
                              className="text-muted-foreground/50 shrink-0"
                            />
                          )}
                        </span>
                        {/* Compact rating bar so the page reads as a
                            visual at-a-glance too, not just numbers. */}
                        <div
                          className="mt-1 h-1 rounded-full bg-muted/30 overflow-hidden max-w-[18rem]"
                          aria-hidden
                        >
                          <div
                            className="h-full bg-primary"
                            style={{ width: `${Math.min(100, row.rating ?? 0)}%` }}
                          />
                        </div>
                      </div>
                      <div className="hidden sm:block text-right text-muted-foreground">
                        {row.games}
                      </div>
                      <div className="hidden sm:block text-right">
                        <span className="text-foreground">{row.wins}</span>
                        <span className="text-muted-foreground">–</span>
                        <span className="text-muted-foreground">
                          {row.losses}
                        </span>
                      </div>
                      {/* Win % — informative, but no longer the sort key. */}
                      <div className="hidden sm:block text-right text-muted-foreground">
                        {(row.winPct * 100).toFixed(0)}%
                      </div>
                      <div className="hidden sm:block text-right text-muted-foreground">
                        {row.ppr != null ? row.ppr.toFixed(1) : "–"}
                      </div>
                      {/* Rating is the ranking metric and the one numeric
                          column kept on mobile. Muted for provisional
                          players (< 3 games) so a thin record reads as
                          unsettled. */}
                      <div
                        className={
                          "text-right font-display " +
                          (row.games < 5 ? "text-muted-foreground" : "")
                        }
                        title={
                          row.games < 5
                            ? "Provisional — needs more games to settle"
                            : undefined
                        }
                      >
                        {(row.rating ?? 0).toFixed(0)}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function TabStrip({
  options,
  value,
  onChange,
}: {
  options: Array<{ id: RangeTab; label: string }>;
  value: RangeTab;
  onChange: (next: RangeTab) => void;
}) {
  return (
    <div className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/20 p-0.5 text-xs">
      {options.map((opt) => {
        const active = opt.id === value;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            className={
              "inline-flex items-center gap-1 px-2.5 py-1 rounded font-display uppercase tracking-wider transition-colors " +
              (active
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground")
            }
            aria-pressed={active}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function Empty({ hint }: { hint: string }) {
  return <div className="p-8 text-center text-muted-foreground text-sm">{hint}</div>;
}
